import {
  IsString,
  MinLength,
  IsOptional,
  IsInt,
  Min,
  Max,
  IsIn,
  IsArray,
  ArrayMinSize,
  ArrayMaxSize,
  Validate,
  ValidateIf,
  ValidatorConstraint,
  type ValidationArguments,
  type ValidatorConstraintInterface,
} from "class-validator";
import type {
  PushCampaignAudienceMode,
  PushCampaignLinkKind,
} from "./pushCampaignLabels";

/** Сумма заголовка, подзаголовка и текста в UTF-8. Совпадает с лимитом сервера. */
export const PUSH_TEXT_MAX_BYTES = 3000;

export function pushTextByteLength(parts: {
  title?: string | null;
  subtitle?: string | null;
  body?: string | null;
}): number {
  return (
    new TextEncoder().encode(parts.title ?? "").length +
    new TextEncoder().encode(parts.subtitle ?? "").length +
    new TextEncoder().encode(parts.body ?? "").length
  );
}

@ValidatorConstraint({ name: "pushTextBytes", async: false })
export class PushTextBytesConstraint implements ValidatorConstraintInterface {
  validate(_value: unknown, args: ValidationArguments): boolean {
    return pushTextByteLength(args.object) <= PUSH_TEXT_MAX_BYTES;
  }

  defaultMessage(args: ValidationArguments): string {
    const size = pushTextByteLength(args.object);
    return `Текст уведомления слишком длинный: ${size} байт из ${PUSH_TEXT_MAX_BYTES}. Сократите заголовок или текст.`;
  }
}

export class PushCampaignFormDto {
  @IsString({ message: "Укажите внутреннее название" })
  @MinLength(1, { message: "Укажите внутреннее название" })
  name!: string;

  @IsString({ message: "Укажите заголовок" })
  @MinLength(1, { message: "Укажите заголовок" })
  title!: string;

  @IsString({ message: "Укажите текст" })
  @MinLength(1, { message: "Укажите текст" })
  @Validate(PushTextBytesConstraint)
  body!: string;

  @IsOptional()
  @IsString()
  subtitle?: string;

  @IsOptional()
  @IsIn(["none", "service_center", "closest", "bookings"])
  link_kind?: PushCampaignLinkKind;

  @ValidateIf((o) => o.link_kind === "service_center")
  @IsString({ message: "Выберите сервисный центр" })
  @MinLength(1, { message: "Выберите сервисный центр" })
  link_service_center_uuid?: string;

  @IsOptional()
  target_business_type?: "car_wash" | "tire_service" | null;

  @IsOptional()
  @IsIn(["all_matching", "client_uuids"])
  audience_mode?: PushCampaignAudienceMode;

  @ValidateIf((o) => o.audience_mode === "client_uuids")
  @IsArray({ message: "Выберите хотя бы одного клиента" })
  @ArrayMinSize(1, { message: "Выберите хотя бы одного клиента" })
  @ArrayMaxSize(500, { message: "Не более 500 клиентов" })
  client_uuids?: string[];

  @IsOptional()
  @IsString()
  audience_query?: string;

  @IsInt()
  @Min(1)
  @Max(168)
  attribution_window_hours = 72;
}

export class PushCampaignTestDto {
  @IsArray()
  @ArrayMinSize(1, { message: "Выберите от 1 до 5 клиентов для теста" })
  @ArrayMaxSize(5, { message: "Выберите от 1 до 5 клиентов для теста" })
  client_uuids!: string[];
}
